import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import styled from '@emotion/styled';
import {
    filter,
    map,
    includes,
    reduce,
    forEach,
    find,
    findIndex,
    groupBy,
    orderBy,
    has
} from 'lodash';

import Icon from '~/components/base/Icon';
import CheckList from '~/components/base/CheckList';

import AccordionGroup from '~/components/AccordionGroup';

import { useLocationPickerContext } from '~/context/LocationPickerContext';

import { getProductPrice } from '~/utilities/products';
import { formatText } from '~/utilities/helpers';

import { HiddenSpan } from '~/styles/helpers';

const Wrapper = styled.div`
    background-color: ${({ theme }) => theme.colors.primary.white};
    padding: 10px;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        padding: 45px;
    }
`;

const Table = styled.table`
    table-layout: fixed;
    overflow: hidden;
    width: 100%;
    display: none;

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        display: table;
    }
`;

const Td = styled.td`
    border: 1px solid ${({ theme }) => theme.colors.text.black};
    height: 63px;
    width: 71px;
    vertical-align: middle;
    text-align: center;
    font-size: 2.3rem;
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    color: ${({ theme }) => theme.colors.text.black};

    &:first-of-type {
        border-left: 0;
        width: auto;
        text-align: left;
        padding-right: 45px;
    }
`;

const InteriorExteriorTd = styled(Td)`
    &:nth-of-type(even) {
        background-color: ${({ theme }) => theme.backgroundColors.lightGray};
    }
`;

const ExteriorTd = styled(Td)`
    &:nth-of-type(even) {
        background-color: #f0f0f0;
    }
`;

const ProductTitleHeader = styled.th`
    border: 1px solid ${({ theme }) => theme.colors.text.black};
    width: 71px;
    color: ${({ theme }) => theme.colors.text.black};

    &:first-of-type {
        width: auto;
    }
`;

const InteriorExteriorGroup = styled.th`
    font-size: 1.6rem;
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.black};
    text-align: center;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.text.black};
    background-color: ${({ theme }) => theme.colors.text.lightBlue};
    border: 1px solid ${({ theme }) => theme.colors.text.lightBlue};
    border-bottom: 0;
    padding: 10px;
`;

const ExteriorGroup = styled(InteriorExteriorGroup)`
    background-color: ${({ theme }) => theme.colors.text.black};
    border-color: ${({ theme }) => theme.colors.text.black};
    color: ${({ theme }) => theme.colors.primary.white};
`;

const InteriorExteriorHeader = styled(ProductTitleHeader)`
    &:nth-of-type(even) {
        background-color: ${({ theme }) => theme.backgroundColors.lightGray};
    }
`;

const ExteriorHeader = styled(ProductTitleHeader)`
    &:nth-of-type(even) {
        background-color: #f0f0f0;
    }
`;

const ThText = styled.span`
    display: block;
    vertical-align: middle;
    transform-origin: 0 0;
    transform: rotate(-90deg) translateX(-315px) translateY(22px);
    white-space: nowrap;
    height: 350px;
    font-size: 2.3rem;
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const EmptyTh = styled.th`
    border-top: 0;
    border-left: 0;
`;

const EmptyTd = styled.td`
    border-top: 0;
    border-left: 0;
`;

const Price = styled.span`
    display: block;
    height: 150px;
    vertical-align: middle;
    transform-origin: 0 0;
    transform: rotate(-90deg) translateX(-115px) translateY(22px);
    font-weight: ${({ theme }) => theme.fontWeights.black};
    font-size: 2.3rem;
    font-family: ${({ theme }) => theme.fonts.heading};

    > span {
        display: block;
        font-size: 1.6rem;
        white-space: nowrap;
    }
`;

const AccordionsWrapper = styled.div`
    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        display: none;
    }
`;

const InteriorExteriorAccordionHeader = styled.h2`
    font-size: 1.6rem;
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.black};
    text-align: center;
    text-transform: uppercase;
    padding: 10px 0 5px;
    background-color: ${({ theme }) => theme.colors.text.lightBlue};
`;

const ExteriorAccordionHeader = styled(InteriorExteriorAccordionHeader)`
    background-color: ${({ theme }) => theme.colors.text.black};
    color: ${({ theme }) => theme.colors.primary.white};
`;

const StyledAccordionGroup = styled(AccordionGroup)`
    h3 {
        font-size: 2.3rem;
        font-weight: ${({ theme }) => theme.fontWeights.black};
    }

    &:not(:last-of-type) {
        margin-bottom: 60px;
    }
`;

const ComparisonTable = ({ productCategory = null }) => {
    const {
        wp: {
            siteSettings: {
                siteSettings: { serviceLabels }
            }
        },
        allWpProduct: { edges }
    } = useStaticQuery(graphql`
        query MyQuery {
            wp {
                siteSettings {
                    siteSettings {
                        serviceLabels {
                            originalLabel
                            labelToDisplay
                        }
                    }
                }
            }
            allWpProduct(
                filter: {
                    productInformation: {
                        comparisonTable: { services: { ne: null } }
                    }
                }
            ) {
                edges {
                    node {
                        id
                        title
                        displayName
                        locations
                        stripePrices {
                            label
                            priceGroup
                            stripeId
                            unitAmount
                            interval
                            interval_count
                            ordinal
                        }
                        productInformation {
                            price
                            comparisonTable {
                                services
                            }
                        }
                        productCategories {
                            nodes {
                                id
                            }
                        }
                        cleaningCategories {
                            nodes {
                                name
                            }
                        }
                        productTypes {
                            nodes {
                                slug
                            }
                        }
                    }
                }
            }
        }
    `);

    const { selectedLocation, locations } = useLocationPickerContext();
    const products = map(
        filter(edges, ({ node }) => {
            const location = selectedLocation || locations[0];
            return (
                includes(node.locations, location?.ampID) &&
                (!productCategory ||
                    find(node.productCategories.nodes, { id: productCategory }))
            );
        }),
        'node'
    );

    const productsSortedByServiceCount = orderBy(
        products,
        (product) => {
            const {
                productInformation: {
                    comparisonTable: { services }
                }
            } = product;

            return services.length;
        },
        'asc'
    );

    const allProducts = groupBy(
        productsSortedByServiceCount,
        ({ cleaningCategories: { nodes: categories } }) => {
            const exterior = find(categories, { name: 'Exterior' });
            const interior = find(categories, { name: 'Interior' });

            if (exterior && interior) {
                return 'interior-exterior';
            }
            if (exterior) {
                return 'exterior';
            }
            if (interior) {
                return 'interior';
            }

            return 'interior-exterior';
        }
    );

    const servicesToProducts = reduce(
        productsSortedByServiceCount,
        (carry, product) => {
            const {
                productInformation: {
                    comparisonTable: { services }
                }
            } = product;
            forEach(services, (service) => {
                const serviceIndex = findIndex(carry, { service });
                if (serviceIndex === -1) {
                    carry.push({ service, products: [product] });
                } else {
                    carry[serviceIndex].products.push(product);
                }
            });
            return carry;
        },
        []
    );

    const sortedServicesToProducts = serviceLabels?.length
        ? reduce(
              serviceLabels,
              (acc, { originalLabel, labelToDisplay }) => {
                  const matchingService = find(
                      servicesToProducts,
                      ({ service }) => service.trim() === originalLabel.trim()
                  );

                  if (!matchingService) return acc;

                  return acc.concat({
                      ...matchingService,
                      service: labelToDisplay
                  });
              },
              []
          )
        : servicesToProducts;

    const getFormattedProductPrice = useCallback(
        (product) => {
            const location = selectedLocation || locations[0];
            return getProductPrice(
                product,
                product.productTypes.nodes[0].slug,
                location?.priceGroup
            );
        },
        [selectedLocation, locations]
    );

    return (
        <Wrapper>
            <Table>
                <thead>
                    <tr>
                        <th aria-hidden="true"></th>
                        {has(allProducts, 'interior-exterior') && (
                            <InteriorExteriorGroup
                                colSpan={
                                    allProducts['interior-exterior'].length
                                }
                                width={
                                    71 * allProducts['interior-exterior'].length
                                }
                            >
                                Interior + Exterior
                            </InteriorExteriorGroup>
                        )}
                        <th width={71} aria-hidden="true"></th>
                        {has(allProducts, 'exterior') && (
                            <ExteriorGroup
                                colSpan={allProducts['exterior'].length}
                                width={71 * allProducts['exterior'].length}
                            >
                                Exterior
                            </ExteriorGroup>
                        )}
                    </tr>
                    <tr>
                        <EmptyTh>
                            <HiddenSpan>Service</HiddenSpan>
                        </EmptyTh>
                        {has(allProducts, 'interior-exterior') &&
                            allProducts['interior-exterior'].map(
                                ({ title, displayName }) => (
                                    <InteriorExteriorHeader key={title}>
                                        <ThText>
                                            {displayName
                                                ? formatText(displayName)
                                                : formatText(title)}
                                        </ThText>
                                    </InteriorExteriorHeader>
                                )
                            )}
                        <th width={71} aria-hidden="true"></th>
                        {has(allProducts, 'exterior') &&
                            allProducts['exterior'].map(
                                ({ title, displayName }) => (
                                    <ExteriorHeader key={title}>
                                        <ThText>
                                            {displayName
                                                ? formatText(displayName)
                                                : formatText(title)}
                                        </ThText>
                                    </ExteriorHeader>
                                )
                            )}
                    </tr>
                </thead>
                <tbody>
                    {sortedServicesToProducts.map(({ service, products }) => (
                        <tr key={service}>
                            <Td>{service}</Td>

                            {has(allProducts, 'interior-exterior') &&
                                allProducts['interior-exterior'].map(
                                    (product) => (
                                        <InteriorExteriorTd key={product.id}>
                                            {find(products, {
                                                id: product.id
                                            }) && (
                                                <Icon
                                                    icon="checkmark"
                                                    color="blue"
                                                    size={29}
                                                />
                                            )}
                                        </InteriorExteriorTd>
                                    )
                                )}
                            <Td></Td>
                            {has(allProducts, 'exterior') &&
                                allProducts['exterior'].map((product) => (
                                    <ExteriorTd key={product.id}>
                                        {find(products, { id: product.id }) && (
                                            <Icon
                                                icon="checkmark"
                                                color="blue"
                                                size={29}
                                            />
                                        )}
                                    </ExteriorTd>
                                ))}
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <EmptyTd aria-hidden="true"></EmptyTd>
                        {has(allProducts, 'interior-exterior') &&
                            allProducts['interior-exterior'].map((product) => (
                                <InteriorExteriorTd key={product.id}>
                                    <Price>
                                        {getFormattedProductPrice(product)}
                                    </Price>
                                </InteriorExteriorTd>
                            ))}
                        <Td></Td>
                        {has(allProducts, 'exterior') &&
                            allProducts['exterior'].map((product) => (
                                <ExteriorTd key={product.id}>
                                    <Price>
                                        {getFormattedProductPrice(product)}
                                    </Price>
                                </ExteriorTd>
                            ))}
                    </tr>
                </tfoot>
            </Table>

            <AccordionsWrapper>
                {has(allProducts, 'exterior') && (
                    <>
                        <InteriorExteriorAccordionHeader>
                            Interior + Exterior
                        </InteriorExteriorAccordionHeader>
                        <StyledAccordionGroup
                            colorTheme="thin"
                            accordions={
                                has(allProducts, 'interior-exterior') &&
                                map(
                                    allProducts['interior-exterior'],
                                    (product) => ({
                                        preTitle:
                                            getFormattedProductPrice(product),
                                        title: product.displayName
                                            ? product.displayName
                                            : product.title,
                                        content: (
                                            <CheckList
                                                items={
                                                    product.productInformation
                                                        .comparisonTable
                                                        .services
                                                }
                                                columns={1}
                                            />
                                        )
                                    })
                                )
                            }
                        />
                    </>
                )}

                {has(allProducts, 'exterior') && (
                    <>
                        <ExteriorAccordionHeader>
                            Exterior
                        </ExteriorAccordionHeader>
                        <StyledAccordionGroup
                            colorTheme="thin"
                            accordions={map(
                                allProducts['exterior'],
                                (product) => ({
                                    preTitle: getFormattedProductPrice(product),
                                    title: product.displayName
                                        ? product.displayName
                                        : product.title,
                                    content: (
                                        <CheckList
                                            items={
                                                product.productInformation
                                                    .comparisonTable.services
                                            }
                                            columns={1}
                                        />
                                    )
                                })
                            )}
                        />
                    </>
                )}
            </AccordionsWrapper>
        </Wrapper>
    );
};

ComparisonTable.propTypes = {
    productCategory: PropTypes.string
};

export default ComparisonTable;
